import React, { useEffect } from 'react';
import _ from 'lodash';
import { Redirect } from 'react-router-dom';
import { PageLink } from 'src/components/links';
import { Page } from 'src/components/matchbox';
import DeleteModal from 'src/components/modals/DeleteModal';
import ConfirmationModal from 'src/components/modals/ConfirmationModal';
import { Loading } from 'src/components/loading/Loading';
import { useAlert, useModal } from 'src/hooks';
import EditForm from './components/EditForm';
import { ROLES } from 'src/constants';

const breadcrumbAction = {
  content: 'Users',
  Component: PageLink,
  to: '/account/users'
};

function EditPage(props) {
  const {
    currentUser,
    handleSubmit,
    isAccountSingleSignOnEnabled,
    accountSingleSignOn,
    getAccountSingleSignOnDetails,
    loading,
    loadingError,
    subaccount,
    getSubaccount,
    submitting,
    updatePending,
    user,
    users,
    listUsers,
    updateUser,
    resetMfa,
    resetTogglePending
  } = props;
  const { closeModal, openModal, isModalOpen, meta = {} } = useModal();
  const { showAlert } = useAlert();
  const isUserToEditAuth0 = user?.auth_migrated;

  const deleteUser = () => {
    const user = props.match.params.id;
    return props.deleteUser(user);
  };

  const handleUserUpdate = ({ access: access_level, is_sso }) => {
    const username = props.match.params.id;

    return updateUser(username, { access_level, is_sso, tfa_enabled: user.tfa_enabled });
  };

  const handleDisableTfa = () => {
    const username = props.match.params.id;
    return updateUser(username, {
      access_level: user.access,
      is_sso: user.is_sso,
      tfa_enabled: false
    }).then(closeModal);
  };

  const handleResetTfa = () => {
    const username = props.match.params.id;
    return resetMfa(username).then(() => {
      closeModal();
      showAlert({
        type: 'success',
        message: `Two-factor authentication for ${username} has been reset.`
      });
    });
  };

  useEffect(() => {
    if (_.isEmpty(accountSingleSignOn)) {
      getAccountSingleSignOnDetails();
    }

    // only request if user visits page directly
    if (_.isEmpty(users)) {
      listUsers();
    }
  }, [accountSingleSignOn, getAccountSingleSignOnDetails, listUsers, users]);

  useEffect(() => {
    if (
      user &&
      (user.access === ROLES.SUBACCOUNT_REPORTING || user.access === ROLES.SUBACCOUNT_DEVELOPER) &&
      (!subaccount || subaccount.id !== user.subaccount_id)
    ) {
      getSubaccount(user.subaccount_id);
    }
  }, [getSubaccount, subaccount, user]);

  if (loading) {
    return <Loading />;
  }

  if (loadingError) {
    return <Redirect to="/account/users" />;
  }

  // Load until we have at least one user
  if (_.isEmpty(users)) {
    return <Loading />;
  }

  if (!user) {
    return <Redirect to="/account/users" />;
  }

  const secondaryActions = [];

  if (!user.isCurrentUser) {
    secondaryActions.push({
      content: 'Delete',
      onClick: () => openModal({ type: 'DeleteUser' })
    });
  }

  if (user.tfa_enabled && !user.isCurrentUser && !isUserToEditAuth0) {
    secondaryActions.push({
      content: 'Disable Two-Factor Authentication',
      onClick: () => openModal({ type: 'DisableTfa' })
    });
  }

  if (user.tfa_enabled && !user.isCurrentUser && isUserToEditAuth0) {
    secondaryActions.push({
      content: 'Reset Two-Factor Authentication',
      onClick: () => openModal({ type: 'ResetTfa' })
    });
  }

  return (
    <Page
      title={user.email}
      breadcrumbAction={breadcrumbAction}
      secondaryActions={secondaryActions}
    >
      <EditForm
        onSubmit={handleSubmit(handleUserUpdate)}
        user={user}
        currentUser={currentUser}
        isAccountSingleSignOnEnabled={isAccountSingleSignOnEnabled}
        submitting={submitting}
        subaccount={subaccount}
      />
      <DeleteModal
        onDelete={deleteUser}
        onCancel={closeModal}
        open={isModalOpen && meta.type === 'DeleteUser'}
        content={
          <p>
            <span>User "</span>
            <span>{props.match.params.id}</span>
            <span>
              " will no longer be able to log in or access this SparkPost account. All API keys
              associated with this user will be transferred to you.
            </span>
          </p>
        }
        title="Are you sure you want to delete this user?"
      />
      <ConfirmationModal
        open={isModalOpen && meta.type === 'DisableTfa'}
        title="Are you sure you want to disable two-factor authentication for this user?"
        content={
          <p>
            Disabling two-factor authentication will also delete their two-factor backup codes. The
            next time they log in, they'll have to use their password or single sign-on if
            available.
          </p>
        }
        onConfirm={handleDisableTfa}
        onCancel={closeModal}
        confirming={updatePending}
        confirmVerb={updatePending ? 'Disabling' : 'Disable'}
      />

      <DeleteModal
        open={isModalOpen && meta.type === 'ResetTfa'}
        title="Reset Multi-Factor Authentication (MFA)"
        content={
          <p>
            Resetting MFA for this user will also delete their Recovery Code. Resetting MFA is
            permanent and cannot be undone
          </p>
        }
        onConfirm={handleResetTfa}
        onCancel={closeModal}
        confirming={resetTogglePending}
        confirmVerb={resetTogglePending ? 'Resetting MFA' : 'Reset MFA'}
      />
    </Page>
  );
}

export default EditPage;
